/**
 * Bot Detection Utility
 * Detects automated browsers, crawlers, and non-human traffic.
 * If detected as bot, the tracker skips all event tracking entirely.
 */

/** Known bot User-Agent patterns (case-insensitive) */
const BOT_UA_PATTERNS: RegExp = new RegExp([
  // Search engine bots
  'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
  'yandexbot', 'sogou', 'exabot', 'ia_archiver',
  // SEO / marketing crawlers
  'semrushbot', 'ahrefsbot', 'mj12bot', 'dotbot', 'rogerbot',
  'screaming frog', 'seokicks', 'sistrix', 'blexbot', 'petalbot',
  // Social media bots
  'facebookexternalhit', 'twitterbot', 'linkedinbot', 'pinterestbot',
  'slackbot', 'telegrambot', 'whatsapp', 'discordbot',
  // Monitoring / uptime
  'uptimerobot', 'pingdom', 'site24x7', 'statuscake', 'newrelicpinger',
  'datadog', 'checkly',
  // Headless browsers (when they don't hide it)
  'headlesschrome', 'phantomjs',
  // Generic bot signatures
  'bot/', 'bot;', 'crawler', 'spider', 'scraper',
  'wget', 'curl/', 'httpie', 'python-requests', 'python-urllib',
  'go-http-client', 'java/', 'apache-httpclient', 'okhttp',
  'node-fetch', 'axios/', 'got/',
  // Prerender / preview
  'prerender', 'lighthouse', 'pagespeed', 'gtmetrix', 'webpagetest',
  // Other known bots
  'applebot', 'amazonbot', 'bytespider', 'gptbot', 'claudebot',
  'anthropic-ai', 'ccbot', 'chatgpt-user', 'ptst/'
].join('|'), 'i');

export interface BotDetectionResult {
  isBot: boolean;
  reason: string;
}

export class BotDetector {

  /**
   * Run all bot detection checks.
   * Returns immediately on first positive detection.
   */
  static detect(): BotDetectionResult {
    // 1. navigator.webdriver — set by Selenium, Puppeteer, Playwright
    if (this.checkWebdriver()) {
      return { isBot: true, reason: 'navigator.webdriver is true' };
    }

    // 2. User-Agent string matching
    const uaResult = this.checkUserAgent();
    if (uaResult) {
      return { isBot: true, reason: `Bot UA pattern: ${uaResult}` };
    }

    // 3. Headless browser signals
    const headlessResult = this.checkHeadlessSignals();
    if (headlessResult) {
      return { isBot: true, reason: headlessResult };
    }

    // 4. Missing/impossible screen dimensions
    if (this.checkPhantomScreen()) {
      return { isBot: true, reason: 'Screen dimensions 0x0' };
    }

    return { isBot: false, reason: '' };
  }

  /**
   * Check navigator.webdriver property
   * Set to true by WebDriver-based automation (Selenium, Puppeteer, Playwright)
   */
  private static checkWebdriver(): boolean {
    return !!(navigator as any).webdriver;
  }

  /**
   * Check User-Agent against known bot patterns
   * Returns the matched pattern fragment or null
   */
  private static checkUserAgent(): string | null {
    const ua = navigator.userAgent || '';

    // Empty or very short UA — suspicious
    if (ua.length < 10) {
      return 'empty/short UA';
    }

    const match = ua.match(BOT_UA_PATTERNS);
    return match ? match[0] : null;
  }

  /**
   * Detect headless browser signals that aren't caught by webdriver check
   */
  private static checkHeadlessSignals(): string | null {
    // PhantomJS globals
    if ((window as any).__phantom || (window as any)._phantom || (window as any).phantom) {
      return 'PhantomJS globals detected';
    }

    // Nightmare.js
    if ((window as any).__nightmare) {
      return 'Nightmare.js detected';
    }

    // Selenium IDE / WebDriver
    if ((document as any).__selenium_unwrapped || 
        (document as any).__webdriver_evaluate || 
        (document as any).__driver_evaluate) {
      return 'Selenium document properties detected';
    }

    // Check for chrome without proper chrome runtime (headless)
    // Only flag if UA says Chrome but chrome object is completely missing
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent || '';
      const isChrome = ua.includes('Chrome/') && !ua.includes('Edg/');
      
      // Chrome UA but zero chrome object → likely headless
      if (isChrome && !(window as any).chrome) {
        return 'Chrome UA without chrome object';
      }
    }

    // Plugins length check — real browsers almost always have plugins
    // navigator.plugins.length === 0 in headless Chrome (pre-v115)
    // Modern headless Chrome fakes plugins, so only flag if combined with other hints
    // (We don't flag this alone to avoid false positives on mobile/privacy browsers)

    return null;
  }

  /**
   * Check for impossible screen dimensions (0x0 = no real screen)
   */
  private static checkPhantomScreen(): boolean {
    return window.screen?.width === 0 && window.screen?.height === 0;
  }
}
